import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import qrcode from "qrcode-generator";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { AuthPanel, AUTH_FIELD } from "../../../shares/landing/LandingWidgets";
import { useAuth } from "../../../../contexts/AuthContext";
import { panelPath } from "../../../../auth/session";
import { authService, loginErrorMessage } from "../../../../api/services/auth";
import { isCanceled } from "../../../../api/client";

const digits = (text) => text.replace(/[۰-۹٠-٩]/g, (digit) =>
  String("۰۱۲۳۴۵۶۷۸۹".includes(digit) ? "۰۱۲۳۴۵۶۷۸۹".indexOf(digit) : "٠١٢٣٤٥٦٧٨٩".indexOf(digit)));

export default function Login({ close }) {
  const { authenticate } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [phase, setPhase] = useState("credentials");
  const [setup, setSetup] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const request = useRef(null);

  useEffect(() => () => request.current?.abort(), []);
  useEffect(() => {
    if (!cooldown) return;
    const timer = setInterval(() => {
      if (Date.now() >= cooldown) setCooldown(0);
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const cancel = () => {
    request.current?.abort();
    setPassword(""); setOtp(""); setSetup(null);
    close();
  };

  const submit = async (event) => {
    event.preventDefault();
    if (request.current || cooldown) return;
    const controller = new AbortController();
    request.current = controller;
    setBusy(true); setError("");
    const options = { signal: controller.signal };
    try {
      const input = { username: digits(username.trim()), password };
      if (phase === "setup" && !setup) {
        const result = await authService.setup(input, options);
        if (controller.signal.aborted) return;
        const qr = qrcode(0, "M");
        qr.addData(result.otpauth_url); qr.make();
        setSetup({ secret: result.secret, image: qr.createDataURL(5, 8) });
        return;
      }
      if (phase !== "credentials") input.otp_token = digits(otp);
      const result = phase === "setup"
        ? await authService.confirm(input, options)
        : await authService.login(input, options);
      if (controller.signal.aborted) return;
      if (result.status === "authenticated") {
        setPassword(""); setOtp(""); setSetup(null);
        authenticate(result.session);
        close();
        navigate(panelPath(result.session), { replace: true });
      } else {
        setOtp("");
        setPhase(result.status === "otp_required" ? "otp" : "setup");
      }
    } catch (failure) {
      if (!isCanceled(failure)) {
        setError(loginErrorMessage(failure, phase));
        if (failure.status === 429) setCooldown(Date.now() + (failure.retryAfterMs ?? 60000));
      }
    } finally {
      if (!controller.signal.aborted) setBusy(false);
      if (request.current === controller) request.current = null;
    }
  };

  return <AuthPanel title="ورود به سامانه" close={cancel}
    description={phase === "credentials" ? "دانش‌آموزان کد ملی و مدیران نام کاربری خود را وارد کنند." : "کد شش‌رقمی برنامه ورود دومرحله‌ای را وارد کنید."}
    footer={<p className="text-base leading-[2] text-ink-muted">برای ثبت‌نام یا دریافت مشخصات ورود با آموزشگاه تماس بگیرید: <a href="tel:09134849505" dir="ltr">09134849505</a></p>}>
    <form onSubmit={submit} className="flex flex-col gap-lg text-md">
      {phase === "credentials" && <>
        <input className={AUTH_FIELD} aria-label="نام کاربری یا کد ملی" placeholder="نام کاربری یا کد ملی"
          autoComplete="username" required value={username} onChange={(event) => setUsername(event.target.value)} disabled={busy} />
        <div className="relative">
          <input className={AUTH_FIELD + " pe-[4.8rem]"} aria-label="رمز عبور" placeholder="رمز عبور"
            type={showPassword ? "text" : "password"} autoComplete="current-password" required
            value={password} onChange={(event) => setPassword(event.target.value)} disabled={busy} />
          <button type="button" className="absolute end-sm top-1/2 -translate-y-1/2"
            aria-label="نمایش رمز عبور" aria-pressed={showPassword} onClick={() => setShowPassword(!showPassword)}>
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </button>
        </div>
      </>}
      {setup && <div className="flex flex-col items-center gap-sm">
        <img src={setup.image} alt="کد QR فعال‌سازی ورود دومرحله‌ای" className="max-w-full" />
        <p>کلید ورود دستی</p><code dir="ltr" className="break-all select-all">{setup.secret}</code>
      </div>}
      {(phase === "otp" || setup) && <input className={AUTH_FIELD} aria-label="کد شش‌رقمی"
        placeholder="کد شش‌رقمی" inputMode="numeric" autoComplete="one-time-code" required
        pattern="[0-9]{6}" maxLength={6} value={otp}
        onChange={(event) => setOtp(digits(event.target.value))} disabled={busy} />}
      {error && <p role="alert" className="text-danger">{error}</p>}
      {cooldown > 0 && <p role="status">ارسال مجدد پس از {new Date(cooldown).toLocaleTimeString("fa-IR")} امکان‌پذیر است.</p>}
      <button disabled={busy || Boolean(cooldown)} className="min-h-[4.4rem] rounded-pill bg-primary p-md text-md font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-60">
        {busy ? "در حال بررسی…" : phase === "setup" && !setup ? "دریافت کد فعال‌سازی" : "ورود به سامانه"}
      </button>
      {phase !== "credentials" && <button type="button" className="min-h-[4.4rem] text-base text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" disabled={busy} onClick={() => {
        setPhase("credentials"); setPassword(""); setOtp(""); setSetup(null); setError("");
      }}>بازگشت به ورود</button>}
    </form>
  </AuthPanel>;
}
